import sodium from 'libsodium-wrappers-sumo';

const encoder = new TextEncoder();
const ATTACHMENT_WRAP_CONTEXT = 'safe-wallet:attachment-key:v1';
const ATTACHMENT_PAYLOAD_CONTEXT = 'safe-wallet:attachment-payload:v1';

function toBase64(bytes: Uint8Array): string {
  return sodium.to_base64(bytes, sodium.base64_variants.URLSAFE_NO_PADDING);
}

function fromBase64(value: string): Uint8Array {
  return sodium.from_base64(value, sodium.base64_variants.URLSAFE_NO_PADDING);
}

function aad(parts: string[]): Uint8Array {
  return encoder.encode(parts.join('|'));
}

export interface EncryptedAttachmentBytes {
  cryptoVersion: 1;
  wrappedAttachmentKey: string;
  keyNonce: string;
  nonce: string;
  ciphertext: Uint8Array;
  checksumSha256: string;
}

export interface EncryptedAttachmentVariant {
  nonce: string;
  ciphertext: Uint8Array;
  checksumSha256: string;
}

export interface EncryptedAttachmentBundle {
  cryptoVersion: 1;
  wrappedAttachmentKey: string;
  keyNonce: string;
  original: EncryptedAttachmentVariant;
  thumbnail?: EncryptedAttachmentVariant;
}

export interface StoredAttachmentEnvelope {
  cryptoVersion: 1;
  wrappedAttachmentKey: string;
  keyNonce: string;
  nonce: string;
  checksumSha256: string;
}

async function sha256Hex(value: Uint8Array): Promise<string> {
  const copy = new Uint8Array(value.byteLength);
  copy.set(value);
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', copy));
  return Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function encryptVariant(
  plaintext: Uint8Array,
  attachmentKey: Uint8Array,
  recordId: string,
  attachmentId: string,
  variant: 'original' | 'thumbnail',
): Promise<EncryptedAttachmentVariant> {
  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    plaintext,
    aad([ATTACHMENT_PAYLOAD_CONTEXT, recordId, attachmentId, variant]),
    null,
    nonce,
    attachmentKey,
  );
  return {
    nonce: toBase64(nonce),
    ciphertext,
    checksumSha256: await sha256Hex(ciphertext),
  };
}

/**
 * Encrypts the original and thumbnail with one random attachment key. The key is
 * wrapped once by the Vault Root Key; each variant receives its own nonce and AAD.
 */
export async function encryptAttachmentVariants(
  original: Uint8Array,
  thumbnail: Uint8Array | undefined,
  vaultRootKey: Uint8Array,
  recordId: string,
  attachmentId: string,
): Promise<EncryptedAttachmentBundle> {
  await sodium.ready;
  const attachmentKey = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_KEYBYTES);
  const keyNonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  try {
    const wrappedKey = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
      attachmentKey,
      aad([ATTACHMENT_WRAP_CONTEXT, recordId, attachmentId]),
      null,
      keyNonce,
      vaultRootKey,
    );
    return {
      cryptoVersion: 1,
      wrappedAttachmentKey: toBase64(wrappedKey),
      keyNonce: toBase64(keyNonce),
      original: await encryptVariant(original, attachmentKey, recordId, attachmentId, 'original'),
      thumbnail: thumbnail
        ? await encryptVariant(thumbnail, attachmentKey, recordId, attachmentId, 'thumbnail')
        : undefined,
    };
  } finally {
    sodium.memzero(attachmentKey);
  }
}

/**
 * Backwards-compatible single-variant helper used by existing tests and callers.
 */
export async function encryptAttachmentBytes(
  plaintext: Uint8Array,
  vaultRootKey: Uint8Array,
  recordId: string,
  attachmentId: string,
  variant: 'original' | 'thumbnail',
): Promise<EncryptedAttachmentBytes> {
  await sodium.ready;
  const attachmentKey = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_KEYBYTES);
  const keyNonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  try {
    const wrappedKey = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
      attachmentKey,
      aad([ATTACHMENT_WRAP_CONTEXT, recordId, attachmentId]),
      null,
      keyNonce,
      vaultRootKey,
    );
    const encrypted = await encryptVariant(plaintext, attachmentKey, recordId, attachmentId, variant);
    return {
      cryptoVersion: 1,
      wrappedAttachmentKey: toBase64(wrappedKey),
      keyNonce: toBase64(keyNonce),
      ...encrypted,
    };
  } finally {
    sodium.memzero(attachmentKey);
  }
}

export async function decryptAttachmentBytes(
  ciphertext: Uint8Array,
  envelope: StoredAttachmentEnvelope,
  vaultRootKey: Uint8Array,
  recordId: string,
  attachmentId: string,
  variant: 'original' | 'thumbnail',
): Promise<Uint8Array> {
  await sodium.ready;
  if (envelope.cryptoVersion !== 1) throw new Error('Unsupported attachment encryption version.');
  const actualChecksum = await sha256Hex(ciphertext);
  if (actualChecksum !== envelope.checksumSha256.toLowerCase()) {
    throw new Error('Encrypted attachment checksum mismatch.');
  }
  const attachmentKey = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
    null,
    fromBase64(envelope.wrappedAttachmentKey),
    aad([ATTACHMENT_WRAP_CONTEXT, recordId, attachmentId]),
    fromBase64(envelope.keyNonce),
    vaultRootKey,
  );
  try {
    return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
      null,
      ciphertext,
      aad([ATTACHMENT_PAYLOAD_CONTEXT, recordId, attachmentId, variant]),
      fromBase64(envelope.nonce),
      attachmentKey,
    );
  } catch {
    throw new Error('Encrypted attachment could not be opened.');
  } finally {
    sodium.memzero(attachmentKey);
  }
}
